import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export interface InspectionData {
  siteBrand?: string;
  siteId: number | null;
  siteName: string;
  siteAddress: string;
  sitePhone?: string | null;
  siteEmail?: string | null;
  siteContactName?: string | null;
  siteCoverImagePath?: string | null;
  assetId: string;
  equipmentLocation: string;
  refrigerantType: string;
  holdingTemp: string;
  maintenanceInterval: 'six_monthly' | 'annual' | '';
  safetyAccess: 'yes' | 'no' | '';
  isolation: 'yes' | 'not_required' | '';
  preServiceNotes: string;
  fgasLogbook: 'yes' | 'digital_checked' | 'no' | '';
  tasks: {
    condenserCoil: boolean;
    evaporatorCoil: boolean;
    condensateDrains: boolean;
    leakDetection: boolean;
    doorSeals: boolean;
    operationalPressures: boolean;
    electricalTerminals: boolean;
    insulationResistance: boolean;
    safetyCutouts: boolean;
    alarmsReliefValves: boolean;
    compressorOil: boolean;
    defrostElements: boolean;
  };
  leakStatus: 'none' | 'repaired' | 'isolated' | '';
  systemOperational: 'yes' | 'no' | '';
  notes: string;
  engineerSignature: string;
  clientSignature: string;
  inspectionDate: string;
}

export interface Inspection {
  id: number;
  engineerId: number;
  engineerName: string;
  status: 'draft' | 'completed';
  data: InspectionData;
  createdAt: string;
  updatedAt: string;
}

export const defaultInspectionData: InspectionData = {
  siteBrand: '',
  siteId: null,
  siteName: '',
  siteAddress: '',
  sitePhone: '',
  siteEmail: '',
  siteContactName: '',
  siteCoverImagePath: null,
  assetId: '',
  equipmentLocation: '',
  refrigerantType: '',
  holdingTemp: '',
  maintenanceInterval: '',
  safetyAccess: '',
  isolation: '',
  preServiceNotes: '',
  fgasLogbook: '',
  tasks: {
    condenserCoil: false,
    evaporatorCoil: false,
    condensateDrains: false,
    leakDetection: false,
    doorSeals: false,
    operationalPressures: false,
    electricalTerminals: false,
    insulationResistance: false,
    safetyCutouts: false,
    alarmsReliefValves: false,
    compressorOil: false,
    defrostElements: false,
  },
  leakStatus: '',
  systemOperational: '',
  notes: '',
  engineerSignature: '',
  clientSignature: '',
  inspectionDate: new Date().toISOString().split('T')[0],
};

export function useInspections() {
  return useQuery<Inspection[]>({
    queryKey: ["inspections"],
    queryFn: async () => {
      const res = await fetch(`${import.meta.env.BASE_URL}api/ppm/inspections`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch inspections");
      return res.json();
    },
  });
}

export function useInspection(id: number) {
  return useQuery<Inspection>({
    queryKey: ["inspection", id],
    queryFn: async () => {
      const res = await fetch(`${import.meta.env.BASE_URL}api/ppm/inspections/${id}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch inspection");
      return res.json();
    },
    enabled: !!id,
  });
}

export function useCreateInspection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { status: 'draft' | 'completed', data: InspectionData }) => {
      const res = await fetch(`${import.meta.env.BASE_URL}api/ppm/inspections`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Failed to create inspection");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inspections"] });
    },
  });
}

export function useUpdateInspection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: { status: 'draft' | 'completed', data: InspectionData } }) => {
      const res = await fetch(`${import.meta.env.BASE_URL}api/ppm/inspections/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Failed to update inspection");
      }
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["inspections"] });
      queryClient.setQueryData(["inspection", data.id], data);
    },
  });
}

export function useDeleteInspection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${import.meta.env.BASE_URL}api/ppm/inspections/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Failed to delete inspection");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inspections"] });
    },
  });
}
